<script setup lang="ts">
import { onUnmounted, ref } from "vue";
import { api, errorText } from "../api";
import ImageValue from "./ImageValue.vue";
const props = defineProps<{
  dataset: string;
  field: string;
  label: string;
  form?: string;
  disabled?: boolean;
}>();
const value = defineModel<string>({ required: true }),
  emit = defineEmits<{ busy: [boolean] }>(),
  busy = ref(false),
  error = ref("");
const galleryInput = ref<HTMLInputElement>(),
  cameraInput = ref<HTMLInputElement>();
function chooseImage(source: "gallery" | "camera") {
  if (props.disabled || busy.value) return;
  (source === "camera" ? cameraInput : galleryInput).value?.click();
}
let attempt = 0;
async function upload(event: Event) {
  const input = event.target as HTMLInputElement,
    file = input.files?.[0];
  input.value = "";
  if (!file || props.disabled || busy.value) return;
  error.value = "";
  if (file.size > 5 * 1024 * 1024) {
    error.value = "Billedet må højst fylde 5 MB.";
    return;
  }
  const id = ++attempt;
  busy.value = true;
  emit("busy", true);
  try {
    const body = new FormData();
    body.append("file", file);
    const next = await api(
      `/api/datasets/${props.dataset}/images?field=${props.field}${props.form ? "&form=" + props.form : ""}`,
      { method: "POST", body },
    );
    if (id === attempt) value.value = next.id;
  } catch (e) {
    if (id === attempt) error.value = errorText(e);
  } finally {
    if (id === attempt) {
      busy.value = false;
      emit("busy", false);
    }
  }
}
onUnmounted(() => {
  attempt++;
  emit("busy", false);
});
</script>
<template>
  <div class="record-image-input" :aria-busy="busy">
    <ImageValue
      v-if="value"
      :value="value"
      :label="label"
      :dataset="dataset"
      :form="form"
    />
    <input
      ref="galleryInput"
      class="image-gallery-input"
      type="file"
      accept="image/png,image/jpeg,image/webp"
      :aria-label="'Upload ' + label"
      :disabled="disabled || busy"
      @change="upload"
    />
    <input
      ref="cameraInput"
      class="image-camera-input"
      type="file"
      accept="image/png,image/jpeg,image/webp"
      capture="environment"
      :aria-label="'Tag billede: ' + label"
      :disabled="disabled || busy"
      hidden
      @change="upload"
    />
    <div class="image-mobile-actions" role="group" :aria-label="label">
      <button
        type="button"
        class="button secondary"
        :disabled="disabled || busy"
        @click="chooseImage('gallery')"
      >
        Vælg fra galleri
      </button>
      <button
        type="button"
        class="button secondary"
        :disabled="disabled || busy"
        @click="chooseImage('camera')"
      >
        Tag billede
      </button>
    </div>
    <p class="muted small">
      PNG, JPG eller WebP · højst 5 MB. Billedet gemmes, når du gemmer rækken.
    </p>
    <p v-if="busy" class="muted small" role="status">Behandler billede…</p>
    <button
      v-if="value"
      type="button"
      class="text-button"
      :disabled="disabled || busy"
      @click="value = ''"
    >
      Fjern billede
    </button>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
  </div>
</template>
<style scoped lang="scss" src="../styles/components/ImageInput.scss"></style>
